import { useCallback } from 'react'

import { useRudderstackAnalytics } from '@/hooks/useRudderstackAnalytics'

// Kettle autocomplete results separate address lines with literal `<br>` tags;
// tracked properties want the display form ("Pinehurst Resort, Pinehurst, NC, USA").
const cleanAddress = (formattedAddress?: string): string | undefined =>
  formattedAddress?.replaceAll('<br>', ', ')

const pageUrl = (): string => (typeof window !== 'undefined' ? window.location.href : '')

export interface CheckoutStartedProperties {
  tripType?: string
  origin?: string
  destination?: string
}

export interface UseBookingWidgetTracking {
  trackOriginEntered: (formattedAddress?: string) => void
  trackDestinationEntered: (formattedAddress?: string) => void
  trackTripTypeSelected: (tripType?: string) => void
  trackCheckoutStarted: (properties: CheckoutStartedProperties) => void
}

/**
 * Booking-widget RudderStack events, shared by the classic multi-brand widget
 * (BookingWidgetPage) and the Ship&Play home-hero widget (ShippingWidget) so
 * neither duplicates the event names/payloads. `brand` (and `cart_id`) are
 * injected by useRudderstackAnalytics' track wrapper. Callers pass their own
 * tripType default — the classic widget falls back to 'roundtrip', the
 * Ship&Play widget always sends 'oneway' (no trip type selector; the funnel
 * seeds a one-way leg).
 */
export const useBookingWidgetTracking = (): UseBookingWidgetTracking => {
  const { track } = useRudderstackAnalytics()

  const trackOriginEntered = useCallback(
    (formattedAddress?: string): void => {
      track?.('booking_widget_origin_entered', {
        origin_address: cleanAddress(formattedAddress),
        url: pageUrl(),
      })
    },
    [track],
  )

  const trackDestinationEntered = useCallback(
    (formattedAddress?: string): void => {
      track?.('booking_widget_destination_entered', {
        destination_address: cleanAddress(formattedAddress),
        url: pageUrl(),
      })
    },
    [track],
  )

  const trackTripTypeSelected = useCallback(
    (tripType?: string): void => {
      track?.('booking_widget_trip_type_selected', {
        trip_type: tripType,
        url: pageUrl(),
      })
    },
    [track],
  )

  const trackCheckoutStarted = useCallback(
    ({ tripType, origin, destination }: CheckoutStartedProperties): void => {
      track?.('Checkout Started', {
        trip_type: tripType,
        origin: cleanAddress(origin),
        destination: cleanAddress(destination),
      })
    },
    [track],
  )

  return {
    trackOriginEntered,
    trackDestinationEntered,
    trackTripTypeSelected,
    trackCheckoutStarted,
  }
}
